// How a setup card reads on screen (v3 `orpheusSetupCardPresentation.ts`). The backend sends each
// row's key, its order and the raw write inputs; the labels and the formatting are the screen's job.
// Labels are the product form's own (the alert wizard's review step, the KPI create form).
//
// Pure: every word comes through `SetupLabels.t`, and a metric's product name through
// `SetupLabels.metric` (the report metric catalog), so the copy lives in `orpheus.setup.*`.
import type { SetupCard } from '../models/orpheusSetup.types'

import {
  ALERT_WRITE,
  channelLabel,
  KPI_TARGET_UPDATE,
  KPI_WRITE,
  list,
  scalar,
  setupSubject,
  type SetupLabels,
} from './orpheusSetupCardRows'

export {
  ALERT_WRITE,
  confirmLabel,
  KPI_TARGET_UPDATE,
  KPI_WRITE,
  type CardRowView,
  type SetupLabels,
  type Translate,
} from './orpheusSetupCardRows'
export { cardRows, hasSetupDraft, incompleteCard } from './orpheusSetupCardSlots'

const K = 'orpheus.setup'

export function cardTitle(labels: SetupLabels, card: SetupCard): string {
  const nameRow = card.rows.find((row) => row.key === 'alert_name' || row.key === 'kpi_name')
  // An update lists only what changes, so the name comes from the saved alert's values.
  const details = card.formValues?.content_details
  const savedName =
    details && typeof details === 'object' ? (details as Record<string, unknown>).name : undefined
  const name = nameRow ? scalar(Object.values(nameRow.fields)[0]) : scalar(savedName)
  if (name) return name
  const subject = setupSubject(labels, card.capabilityId)
  return labels.t(`${K}.title.${card.action === 'update' ? 'update' : 'new'}`, { subject })
}

export function cardCostLabel(labels: SetupLabels, card: SetupCard): string {
  const cost = card.cost ?? {}
  const required = cost.credits_required ?? cost.max_credits
  if (typeof required !== 'number') return ''
  return labels.t(required === 1 ? `${K}.cost.upToOne` : `${K}.cost.upTo`, { count: required })
}

export interface SetupRouteLink {
  name: string
  query?: Record<string, string>
}

export interface ConfirmedSummary {
  title: string
  delivery: string
  link: { label: string; to: SetupRouteLink } | null
}

export const ALERTS_ROUTE_NAME = 'Alerts'
export const KPI_TRACKING_ROUTE_NAME = 'KPITracking'

/** What the chat says after the button saved the draft: what, where it reports, where it lives. */
export function confirmedSummary(
  labels: SetupLabels,
  card: SetupCard,
  result: Record<string, unknown> | null,
): ConfirmedSummary {
  const { t } = labels
  const subject = setupSubject(labels, card.capabilityId)
  const title = t(`${K}.saved.${card.action === 'update' ? 'updated' : 'created'}`, {
    subject: subject.charAt(0).toUpperCase() + subject.slice(1),
    name: cardTitle(labels, card),
  })

  let delivery = ''
  if (card.capabilityId === ALERT_WRITE) {
    const afterRaw = result?.after
    const after = (afterRaw && typeof afterRaw === 'object' ? afterRaw : {}) as Record<
      string,
      unknown
    >
    const notification = card.rows.find((row) => row.key === 'notification')?.fields ?? {}
    const channels = list(after.channel_type ?? notification.channel_type).map((channel) =>
      channelLabel(labels, channel),
    )
    const emails = list(after.allowed_emails ?? notification.allowed_emails).map((email) =>
      scalar(email),
    )
    const email = t(`${K}.channel.email`)
    if (channels.length) {
      delivery = t(`${K}.saved.delivery`, {
        channels: channels
          .map((channel) =>
            channel === email && emails.length
              ? t(`${K}.saved.emailTo`, { emails: emails.join(', ') })
              : channel,
          )
          .join(', '),
      })
    }
  }

  // `alert-<id>` names the saved alert (the write's result, or the draft's target on an edit); the
  // Alerts page opens it in its form. A reloaded card has no result, so it links the page.
  const alertId = /^alert-(\d+)$/.exec(scalar(result?.alert_ref ?? card.target))?.[1]
  let link: ConfirmedSummary['link'] = null
  if (card.capabilityId === ALERT_WRITE) {
    link = alertId
      ? {
          label: t(`${K}.action.openInForm`),
          to: { name: ALERTS_ROUTE_NAME, query: { edit_alert: alertId } },
        }
      : { label: t(`${K}.action.openAlerts`), to: { name: ALERTS_ROUTE_NAME } }
  } else if (card.capabilityId === KPI_WRITE || card.capabilityId === KPI_TARGET_UPDATE) {
    link = { label: t(`${K}.action.openKpiTracking`), to: { name: KPI_TRACKING_ROUTE_NAME } }
  }

  return { title, delivery, link }
}

/** Where the product's own form for this write lives. */
export function formLink(card: SetupCard): Required<SetupRouteLink> | null {
  if (card.capabilityId === ALERT_WRITE) {
    const alertId = /^alert-(\d+)$/.exec(card.target ?? '')?.[1]
    return {
      name: ALERTS_ROUTE_NAME,
      query: alertId ? { edit_alert: alertId } : { create: 'alert' },
    }
  }
  if (card.capabilityId === KPI_WRITE) {
    return { name: KPI_TRACKING_ROUTE_NAME, query: { create: 'kpi' } }
  }
  return null
}

// The alert wizard's names for the windows its review step reads out.
const ALERT_WINDOW_KEYS: Record<string, string> = {
  daily: 'daily',
  weekly: 'weekly',
}

/** The alert form's own timing sentence, for the window the draft evaluates. */
export function alertTimingSentence(labels: SetupLabels, card: SetupCard): string {
  if (card.capabilityId !== ALERT_WRITE) return ''
  const frequency = scalar(
    card.rows.find((row) => row.key === 'frequency')?.fields.frequency,
  ).toLowerCase()
  const windowKey = ALERT_WINDOW_KEYS[frequency]
  return windowKey
    ? labels.t(`${K}.timing`, { window: labels.t(`${K}.timingWindow.${windowKey}`) })
    : ''
}

export function existingAlertNames(card: SetupCard): string {
  return card.existingAlerts
    .map((alert) => scalar(alert.name))
    .filter(Boolean)
    .join(', ')
}

/**
 * What "Try again" sends: a refused draft is spent, so only a fresh preview can be saved. Sent to
 * the model, not shown as product copy, so it stays the same sentence whatever the locale.
 */
export const TRY_AGAIN_MESSAGE = 'Prepare the same setup again.'

/** What editing a row sends: the change goes back as one chat turn. Read by the model. */
export function rowEditMessage(label: string, value: string): string {
  return `Change ${label} to ${value}`
}
